import editPencil from "@assets/edit-pencil.svg";
import AddIcon from "@mui/icons-material/Add";
import Delete from "@mui/icons-material/DeleteTwoTone";
import { Chip, css, IconButton, TextField } from "@mui/material";
import { useEffect, useState } from "react";
import { trpc } from "../../../utils/trpc";
import { Modal } from "../Modal";
import { PrimaryButton, SecondaryButton, TertiaryButton } from "../buttons";
import { FlexColContainer, FlexRowContainer } from "../layout";

type TagOption = { id: number; name: string };

interface TagManagementModalProps {
  tags: TagOption[];
  icon?: React.ReactNode;
  annotationId?: number;
  activeTagIds?: number[];
  buttonLabel?: string;
}

export const TagManagementModal = ({
  tags,
  icon,
  annotationId,
  activeTagIds = [],
  buttonLabel = "Manage",
}: TagManagementModalProps) => {
  const [isManageTagsModalOpen, setIsManageTagsModalOpen] = useState(false);
  const [isAddTagModalOpen, setIsAddTagModalOpen] = useState(false);
  const [deletingTagId, setDeletingTagId] = useState<number | null>(null);
  const [editingTagId, setEditingTagId] = useState<number | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [newTagValue, setNewTagValue] = useState("");
  const [annotationTagIds, setAnnotationTagIds] =
    useState<number[]>(activeTagIds);

  const createTag = trpc.createTag.useMutation();
  const updateTag = trpc.updateTag.useMutation();
  const {
    mutate: updateAnnotationTags,
    isLoading: isUpdateAnnotationTagsLoading,
  } = trpc.updateAnnotationTags.useMutation();
  const { mutate: deleteTag, isLoading: isDeleteTagLoading } =
    trpc.deleteTag.useMutation();

  const sortedTags = [...tags].sort((a, b) => a.name.localeCompare(b.name));

  const openRenameModal = (tag: TagOption) => {
    setEditingTagId(tag.id);
    setRenameValue(tag.name);
  };

  const closeRenameModal = () => {
    setEditingTagId(null);
    setRenameValue("");
  };

  const handleCreateTag = () => {
    const trimmed = newTagValue.trim();
    if (!trimmed) return;

    createTag.mutate(
      { name: trimmed },
      {
        onSuccess: () => {
          setNewTagValue("");
          setIsAddTagModalOpen(false);
        },
      },
    );
  };

  const handleRenameTag = (tag: TagOption) => {
    const trimmed = renameValue.trim();
    if (!trimmed || trimmed === tag.name) {
      closeRenameModal();
      return;
    }

    updateTag.mutate(
      { tagId: tag.id, name: trimmed },
      { onSuccess: () => closeRenameModal() },
    );
  };

  const handleDeleteTag = (tag: TagOption) => {
    deleteTag(
      { tagIds: [tag.id] },
      { onSuccess: () => setDeletingTagId(null) },
    );
  };

  const handleApplyTagsToAnnotation = (selectedTagIds: number[]) => {
    if (annotationId == null) return;
    setAnnotationTagIds(selectedTagIds);
    updateAnnotationTags({ annotationId, tagIds: selectedTagIds });
  };

  useEffect(() => {
    if (isManageTagsModalOpen) {
      setAnnotationTagIds(activeTagIds);
    }
  }, [isManageTagsModalOpen, activeTagIds]);

  return (
    <Modal
      key="manageTagsModal"
      isOpen={isManageTagsModalOpen}
      onClose={() => {
        setIsManageTagsModalOpen(false);
      }}
      renderButton={() =>
        icon ? (
          <IconButton
            onClick={() => setIsManageTagsModalOpen(true)}
            title="Manage tags"
            size="small"
            sx={{
              borderRadius: "6px",
              height: "24px",
              width: "24px",
            }}
          >
            {icon}
          </IconButton>
        ) : (
          <SecondaryButton
            onClick={() => setIsManageTagsModalOpen(true)}
            title="Manage tags"
            css={css`
              font-size: 11px;
              text-transform: none;
            `}
          >
            {buttonLabel}
          </SecondaryButton>
        )
      }
      header={
        <FlexRowContainer
          style={{ justifyContent: "space-between", width: "100%" }}
        >
          <h2>Manage tags</h2>
          <FlexRowContainer style={{ justifyContent: "flex-end" }}>
            <Modal
              key="addTagModal"
              isOpen={isAddTagModalOpen}
              onClose={() => {
                setIsAddTagModalOpen(false);
                setNewTagValue("");
              }}
              renderButton={() => (
                <PrimaryButton
                  onClick={() => setIsAddTagModalOpen(true)}
                  title="Add tag"
                >
                  <AddIcon fontSize="small" />
                  New tag
                </PrimaryButton>
              )}
              header={<h2>Add tag</h2>}
              content={
                <FlexColContainer
                  css={({ spacing }) => css`
                    gap: ${spacing(2)};
                    padding-top: ${spacing(1)};
                  `}
                >
                  <TextField
                    label="Tag name"
                    value={newTagValue}
                    onChange={(event) => {
                      setNewTagValue(event.target.value);
                    }}
                    size="small"
                    autoFocus
                  />
                </FlexColContainer>
              }
              footer={
                <FlexRowContainer style={{ justifyContent: "flex-end" }}>
                  <TertiaryButton
                    onClick={() => {
                      setIsAddTagModalOpen(false);
                      setNewTagValue("");
                    }}
                  >
                    Cancel
                  </TertiaryButton>
                  <PrimaryButton
                    onClick={handleCreateTag}
                    disabled={createTag.isLoading || !newTagValue.trim()}
                  >
                    Add
                  </PrimaryButton>
                </FlexRowContainer>
              }
            />
          </FlexRowContainer>
        </FlexRowContainer>
      }
      content={
        <FlexColContainer
          css={({ spacing }) => css`
            gap: ${spacing(2)};
          `}
        >
          {sortedTags.length === 0 ? (
            <p>You don't have any tags yet. Create one to get started!</p>
          ) : (
            sortedTags.map((tag) => (
              <FlexRowContainer key={tag.id}>
                <Chip
                  css={({ breakpoints }) => css`
                    overflow: hidden;
                    @media (max-width: ${breakpoints.values.md}px) {
                      max-width: 180px;
                    }
                  `}
                  key={tag.id}
                  label={tag.name}
                  disabled={isUpdateAnnotationTagsLoading}
                  onClick={
                    annotationId != null
                      ? () => {
                          if (annotationTagIds.includes(tag.id)) {
                            handleApplyTagsToAnnotation(
                              annotationTagIds.filter((id) => id !== tag.id),
                            );
                          } else {
                            handleApplyTagsToAnnotation([
                              ...annotationTagIds,
                              tag.id,
                            ]);
                          }
                        }
                      : undefined
                  }
                  variant={
                    annotationTagIds.includes(tag.id) || annotationId == null
                      ? "filled"
                      : "outlined"
                  }
                />
                <FlexRowContainer>
                  <Modal
                    key={`renameTagModal-${tag.id}`}
                    isOpen={editingTagId === tag.id}
                    onClose={closeRenameModal}
                    renderButton={() => (
                      <TertiaryButton
                        title="Edit tag"
                        onClick={() => openRenameModal(tag)}
                        css={({ breakpoints }) => css`
                          width: 116px;
                          @media (max-width: ${breakpoints.values.sm}px) {
                            width: 32px;
                            min-width: 0;
                            padding: 0;
                          }
                        `}
                      >
                        <img
                          src={editPencil}
                          alt="Edit tag"
                          style={{ opacity: 0.5 }}
                        />
                        <span
                          css={({ breakpoints }) => css`
                            @media (max-width: ${breakpoints.values.sm}px) {
                              display: none;
                            }
                          `}
                        >
                          Rename
                        </span>
                      </TertiaryButton>
                    )}
                    header={<h2>Rename tag</h2>}
                    content={
                      <FlexColContainer
                        css={({ spacing }) => css`
                          gap: ${spacing(2)};
                        `}
                      >
                        <p style={{ fontSize: "14px" }}>
                          Previous name: <strong>{tag.name}</strong>
                        </p>
                        <TextField
                          label="Tag name"
                          value={renameValue}
                          onChange={(event) => {
                            setRenameValue(event.target.value);
                          }}
                          size="small"
                          autoFocus
                        />
                      </FlexColContainer>
                    }
                    footer={
                      <FlexRowContainer style={{ justifyContent: "flex-end" }}>
                        <TertiaryButton onClick={closeRenameModal}>
                          Cancel
                        </TertiaryButton>
                        <PrimaryButton
                          onClick={() => handleRenameTag(tag)}
                          disabled={updateTag?.isLoading}
                        >
                          Rename
                        </PrimaryButton>
                      </FlexRowContainer>
                    }
                  />
                  <Modal
                    key={`deleteTagModal-${tag.id}`}
                    isOpen={deletingTagId === tag.id}
                    onClose={() => {
                      setDeletingTagId(null);
                    }}
                    renderButton={() => (
                      <IconButton
                        onClick={() => setDeletingTagId(tag.id)}
                        disabled={isDeleteTagLoading}
                        title="Delete tag"
                        size="small"
                      >
                        <Delete sx={{ height: "24px" }} />
                      </IconButton>
                    )}
                    header={<h2>Delete tag</h2>}
                    content={
                      <FlexColContainer
                        css={({ spacing }) => css`
                          gap: ${spacing(2)};
                        `}
                      >
                        {`Are you sure you want to delete this tag? This will remove the tag from all annotations.`}
                      </FlexColContainer>
                    }
                    footer={
                      <PrimaryButton
                        onClick={() => handleDeleteTag(tag)}
                        disabled={isDeleteTagLoading}
                      >
                        Delete
                      </PrimaryButton>
                    }
                  />
                </FlexRowContainer>
              </FlexRowContainer>
            ))
          )}
        </FlexColContainer>
      }
      footer={
        <FlexRowContainer style={{ justifyContent: "flex-end" }}>
          <PrimaryButton
            onClick={() => {
              setIsManageTagsModalOpen(false);
            }}
          >
            Done
          </PrimaryButton>
        </FlexRowContainer>
      }
    />
  );
};
